import React from 'react';
import PropTypes from 'prop-types';

// JS scripts needing to be run in the head (Newrelic and Heap)
// These could also be placed implicitly in the Hemlet with <script - but the scripts are long so placing in another file
import innerHtmlScripts from './innerHtmlScripts';

export const HeadComponent = ({
  env,
  Container,
  metaRobotsNoIndexNoFollow,
  siteTitleFirst,
  title,
  description,
  canonical,
  optimizelyProjectId,
  useReCaptcha,
}) => {
  const {
    basePath, nmxTemplateVersion, adobeLaunchScript, public: envPublic,
  } = env;

  return (
    <Container script={innerHtmlScripts(env)}>
      <meta charset="utf-8" />
      <meta http-equiv="X-UA-Compatible" content="IE=edge" />
      {metaRobotsNoIndexNoFollow && (
        <meta name="robots" content="noindex, nofollow"></meta>
      )}

      {siteTitleFirst ? (
        <title>Northwestern Mutual | {title}</title>
      ) : (
        <title>{title} | Northwestern Mutual</title>
      )}

      {canonical && <link rel="canonical" href={canonical} />}

      <meta name="description" content={description} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {optimizelyProjectId && (
        <link
          rel="preload"
          href={`https://cdn.optimizely.com/js/${optimizelyProjectId}.js`}
          as="script"
        />
      )}
      {optimizelyProjectId && (
        <script
          src={`https://cdn.optimizely.com/js/${optimizelyProjectId}.js`}></script>
      )}

      <script src={adobeLaunchScript} async></script>

      {useReCaptcha && (
        <script
          src={`https://www.google.com/recaptcha/api.js?render=${envPublic.recaptchaV3}`}
          async
          defer></script>
      )}

      {/* FAVICON */}
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/apple-touch-icon.png`}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/favicon-32x32.png`}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/favicon-16x16.png`}
      />
      <link
        rel="manifest"
        href={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/site.webmanifest`}
      />
      <link
        rel="mask-icon"
        href={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/safari-pinned-tab.svg`}
        color="#00487b"
      />
      <link
        rel="shortcut icon"
        href={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/favicon.ico`}
      />
      <meta name="apple-mobile-web-app-title" content="Northwestern Mutual" />
      <meta name="application-name" content="Northwestern Mutual" />
      <meta name="msapplication-TileColor" content="#da532c" />
      <meta
        name="msapplication-config"
        content={`${basePath}/template/assets/${nmxTemplateVersion}/images/favicon/browserconfig.xml`}
      />
      <meta name="theme-color" content="#ffffff"></meta>
      {/* END FAVICON */}

      <link
        href="https://use.typekit.net/hql5rrn.css"
        rel="stylesheet"
        type="text/css"
      />
      <link
        href={`${basePath}/template/assets/${nmxTemplateVersion}/css/nmx-template.css`}
        rel="stylesheet"
        type="text/css"
      />

      {/* schema/base.schema.partial */}
      <script type="application/ld+json">
        {`{
              '@context': 'https://schema.org',
              '@type': 'Corporation',
              name: 'Northwestern Mutual',
              legalName: 'The Northwestern Mutual Life Insurance Company',
              url: ${basePath},
              logo: '${basePath}/logo-horizontal-navy.png',
              contactPoint: [
                {
                  '@type': 'ContactPoint',
                  telephone: '+1-866-950-4644',
                  contactType: 'customer service'
                }
              ],
              diversityPolicy: '${basePath}/our-commitment-to-diversity/',
              sameAs: [
                'https://www.facebook.com/northwesternmutual',
                'https://twitter.com/nm_financial',
                'https://www.linkedin.com/company/northwestern-mutual',
                'https://instagram.com/northwesternmutual'
              ]
            }`}
      </script>
      <script type="application/ld+json">
        {`{
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Northwestern Mutual',
            url: '${basePath}/',
            potentialAction: {
              '@type': 'SearchAction',
              target: '${basePath}/?q={search_term}',
              'query-input': 'required name=search_term'
            }
        }`}
      </script>
    </Container>
  );
};

HeadComponent.propTypes = {
  /** Env vars used in head */
  env: PropTypes.shape({
    basePath: PropTypes.string.isRequired,
    nmxTemplateVersion: PropTypes.string.isRequired,
    newRelicApplicationId: PropTypes.string.isRequired,
    newRelicLicenseKey: PropTypes.string.isRequired,
    heapAppId: PropTypes.string.isRequired,
    adobeLaunchScript: PropTypes.string.isRequired,
    public: PropTypes.shape({
      recaptchaV3: PropTypes.string,
    }).isRequired,
  }).isRequired,
  /** container for head component. Replace with react-helmet or react-scripts helment if not wanting default <head /> */
  Container: PropTypes.any,
  metaRobotsNoIndexNoFollow: PropTypes.bool,
  siteTitleFirst: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  canonical: PropTypes.string,
  optimizelyProjectId: PropTypes.string,
  useReCaptcha: PropTypes.bool,
};

HeadComponent.defaultProps = {
  Container: 'head',
  metaRobotsNoIndexNoFollow: false,
  siteTitleFirst: false,
};

export default HeadComponent;
